/*
 * @Author: Zhouqi
 * @Date: 2025-12-10 10:22:39
 * @LastEditors: Ryan
 * @LastEditTime: 2026-02-02
 */
import dynamic from 'next/dynamic';
import React from 'react';

import ImageTranslationSkeleton from '@/3.0/image-translation/_components/image-translation-skeleton';
import { Introduction } from '@/components/container';
import DelayedRender from '@/components/DelayRender';
import Footer from '@/components/Footer';
import Layout from '@/components/layout';
import SEOHead from '@/landing/components/SEOHead';
import { createSoftwareAppSchema } from '@/landing/utils/structuredData';

// 预加载首屏图片资源（第一组对比图）
const PRELOAD_IMAGES = [
  'https://assets.falcocut.ai/ai-image-translator/en-to-de-advertising-banner-before.webp',
  'https://assets.falcocut.ai/ai-image-translator/en-to-de-advertising-banner-after.webp',
];

// 动态加载完整的 ImageTranslation
const ImageTranslation = dynamic(() => import('@/3.0/image-translation/entry'), {
  ssr: false,
  loading: () => (
    <DelayedRender>
      <ImageTranslationSkeleton />
    </DelayedRender>
  ),
});

// 动态加载 SEO 内容
const SEOSections = dynamic(() => import('@/3.0/image-translation/seo'), {
  ssr: true,
});

// SEO 常量
const PAGE_TITLE = 'AI Image Translator - Translate Text in Images | FalcoCut';
const META_DESCRIPTION =
  'Automatically translate text in images while preserving the original design. Perfect for marketing materials, menus, infographics, and documents. Supports 100+ languages with AI-powered accuracy.';
const CURRENT_URL = 'https://falcocut.ai/ai-image-translator';

// 结构化数据
const structuredData = createSoftwareAppSchema({
  name: 'FalcoCut AI Image Translator',
  description: META_DESCRIPTION,
  url: CURRENT_URL,
  ratingValue: '4.8',
  ratingCount: '2650',
  featureList: [
    'Automatic text detection in images',
    'Preserve original layout and design',
    'Support 100+ languages',
    'High-quality AI translation',
    'Batch image translation',
    'Multiple image formats support',
  ],
});

const ImageTranslatorPage: React.FC & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
  getExtraContent?: () => React.ReactNode;
} = () => {
  return (
    <>
      <SEOHead
        title={PAGE_TITLE}
        description={META_DESCRIPTION}
        url={CURRENT_URL}
        imageAlt="FalcoCut AI Image Translator"
        publishedTime="2025-12-10"
        modifiedTime="2026-02-02"
        structuredData={structuredData}
        preloadImages={PRELOAD_IMAGES}
      />

      {/* 客户端动态加载完整组件（保留骨架屏） */}
      <ImageTranslation />
    </>
  );
};

// 定义 getLayout 为直接返回页面本身，使用 Layout
ImageTranslatorPage.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>;

// 定义 SEO 部分和 Footer 作为额外内容，在 Layout 外渲染
ImageTranslatorPage.getExtraContent = () => {
  return (
    <Introduction>
      <SEOSections />
      <Footer />
    </Introduction>
  );
};

export const getStaticProps = async () => {
  return {
    props: {},
  };
};

export default ImageTranslatorPage;
